<template>
  <d-container
    fluid
    class="main-content-container px-4 statistics">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('stats.subtitle') }}</span>
        <h3 class="page-title">{{ $t('stats.title') }}</h3>
      </d-col>

      <d-col
        col
        sm="6"
        class="text-right">
        <d-button
          theme="success"
          class="ml-2 text-nowrap"
          :disabled="downloadInProgress"
          @click.prevent.stop="downloadXlsx">
          <div
            v-if="downloadInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          <template v-if="!downloadInProgress">
            <i class="material-icons">cloud_download</i>
            {{ $t('stats.downloadData') }}
          </template>
        </d-button>
      </d-col>
    </d-row>

    <list-filters
      class="mb-3"
      :config="filtersConfig"
      :isListLoaded="allDataLoaded"
      :view-name="'statistics'"
      :disableStorage="true"
      @init="handleFiltersInit"
      @search="handleFiltersSearch" />

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light fixed-thead">
          <tr>
            <th
              scope="col"
              class="border-0">{{ $t('users.worker') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 110px;"><small>{{ $t('stats.offersCount') }}</small></th>
            <th
              scope="col"
              class="border-0"
              style="width: 110px;"><small>{{ $t('stats.offersAmount') }}</small></th>
            <th
              scope="col"
              class="border-0"
              style="width: 110px;"><small>{{ $t('stats.ordersCount') }}</small></th>
            <th
              scope="col"
              class="border-0"
              style="width: 110px;"><small>{{ $t('stats.ordersAmount') }}</small></th>
            <th
              scope="col"
              class="border-0"
              style="width: 110px;"><small>{{ $t('stats.offersToOrdersCount') }}</small></th>
            <th
              scope="col"
              class="border-0"
              style="width: 110px;"><small>{{ $t('stats.offersToOrdersAmount') }}</small></th>
            <th
              scope="col"
              class="border-0"
              style="width: 110px;"><small>{{ $t('stats.settlementOrdersCount') }}</small></th>
            <th
              scope="col"
              class="border-0"
              style="width: 110px;"><small>{{ $t('stats.settlementOrdersAmount') }}</small></th>
            <th
              scope="col"
              class="border-0"
              style="width: 110px;"><small>{{ $t('stats.endedOrdersCount') }}</small></th>
            <th
              scope="col"
              class="border-0"
              style="width: 110px;"><small>{{ $t('stats.endedOrdersAmount') }}</small></th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <template v-if="isCompareMode">
            <tr class="table-secondary fixed-row">
              <td class="align-middle"><strong>{{ $t('stats.sumRowTitle') }}</strong></td>
              <td
                v-for="(field, index) of statsFields"
                :key="'table-cell-stats-sum-' + index">
                <strong>
                  <span
                    :class="{
                      'text-success': statistics.data.periods[0].stats.sum[field] > statistics.data.periods[1].stats.sum[field],
                      'text-danger': statistics.data.periods[0].stats.sum[field] < statistics.data.periods[1].stats.sum[field],
                      'text-gray': statistics.data.periods[0].stats.sum[field] === statistics.data.periods[1].stats.sum[field],
                    }">
                    {{ statistics.data.periods[0].stats.sum[field] }}
                    <i class="material-icons is-inc">arrow_upward</i>
                    <i class="material-icons is-dec">arrow_downward</i>
                    <i class="material-icons is-eq">remove</i>
                  </span>
                  <br>
                  {{ statistics.data.periods[1].stats.sum[field] }}
                </strong>
              </td>
            </tr>

            <tr
              v-for="(data, index) of statistics.data.periods[0].users"
              :key="'statistics-item-' + index">
              <td class="align-middle">{{ data.name }}</td>
              <td
                v-for="(field, subindex) of statsFields"
                :key="'table-cell-stats-user-sum-' + index + '-' + subindex">
                <span
                  :class="{
                    'text-success': data[field] > statistics.data.periods[1].users[index][field],
                    'text-danger': data[field] < statistics.data.periods[1].users[index][field],
                    'text-gray': data[field] === statistics.data.periods[1].users[index][field],
                  }">
                  {{ data[field] }}
                  <i class="material-icons is-inc">arrow_upward</i>
                  <i class="material-icons is-dec">arrow_downward</i>
                  <i class="material-icons is-eq">remove</i>
                </span>
                <br>
                {{ statistics.data.periods[1].users[index][field] }}
              </td>
            </tr>
            <tr v-if="statistics.data.length === 0">
              <td
                colspan="9"
                class="text-center">
                {{ $t('ui.noInfoToDisplay') }}&hellip;
              </td>
            </tr>

            <tr
              v-for="(type, index) of ['avg', 'min', 'max']"
              :key="'stats-sum-row-' + index"
              class="table-secondary">
              <td class="align-middle"><strong>{{ $t('stats.' + type + 'RowTitle') }}</strong></td>
              <td
                v-for="(field, subindex) of statsFields"
                :key="'table-cell-stats-user-sum-total-' + index + '-' + subindex">
                <strong>
                  <span
                    :class="{
                      'text-success': parseFloat(Number(statistics.data.periods[0].stats[type][field]).toFixed(4)) > parseFloat(Number(statistics.data.periods[1].stats[type][field]).toFixed(4)),
                      'text-danger': parseFloat(Number(statistics.data.periods[0].stats[type][field]).toFixed(4)) < parseFloat(Number(statistics.data.periods[1].stats[type][field]).toFixed(4)),
                      'text-gray': parseFloat(Number(statistics.data.periods[0].stats[type][field]).toFixed(4)) === parseFloat(Number(statistics.data.periods[1].stats[type][field]).toFixed(4)),
                    }">
                    {{ Number(statistics.data.periods[0].stats[type][field]).toFixed(4) }}
                    <i class="material-icons is-inc">arrow_upward</i>
                    <i class="material-icons is-dec">arrow_downward</i>
                    <i class="material-icons is-eq">remove</i>
                  </span>
                  <br>
                  {{ Number(statistics.data.periods[1].stats[type][field]).toFixed(4) }}
                </strong>
              </td>
            </tr>
          </template>
          <template v-else>
            <tr class="table-secondary fixed-row">
              <td class="align-middle"><strong>{{ $t('stats.sumRowTitle') }}</strong></td>
              <td><strong>{{ statistics.stats.sum.offers_count }}</strong></td>
              <td><strong>{{ statistics.stats.sum.offers_amount }}</strong></td>
              <td><strong>{{ statistics.stats.sum.orders_count }}</strong></td>
              <td><strong>{{ statistics.stats.sum.orders_amount }}</strong></td>
              <td><strong>{{ statistics.stats.sum.offers_to_orders_count }}</strong></td>
              <td><strong>{{ statistics.stats.sum.offers_to_orders_amount }}</strong></td>
              <td><strong>{{ statistics.stats.sum.settlement_orders_count }}</strong></td>
              <td><strong>{{ statistics.stats.sum.settlement_orders_amount }}</strong></td>
              <td><strong>{{ statistics.stats.sum.ended_orders_count }}</strong></td>
              <td><strong>{{ statistics.stats.sum.ended_orders_amount }}</strong></td>
            </tr>

            <tr
              v-for="(data, index) of statistics.data"
              :key="'statistics-item-' + index">
              <td class="align-middle">{{ data.name }}</td>
              <td
                class="stats-tooltip"
                @click="showStatsPopup(data.id, 'offers')">{{ data.offers_count }}</td>
              <td
                class="stats-tooltip"
                @click="showStatsPopup(data.id, 'offers')">{{ data.offers_amount }}</td>
              <td
                class="stats-tooltip"
                @click="showStatsPopup(data.id, 'orders')">{{ data.orders_count }}</td>
              <td
                class="stats-tooltip"
                @click="showStatsPopup(data.id, 'orders')">{{ data.orders_amount }}</td>
              <td
                class="stats-tooltip"
                @click="showStatsPopup(data.id, 'offers_to_orders')">{{ data.offers_to_orders_count }}</td>
              <td
                class="stats-tooltip"
                @click="showStatsPopup(data.id, 'offers_to_orders')">{{ data.offers_to_orders_amount }}</td>
              <td
                class="stats-tooltip"
                @click="showStatsPopup(data.id, 'settlement_orders')">{{ data.settlement_orders_count }}</td>
              <td
                class="stats-tooltip"
                @click="showStatsPopup(data.id, 'settlement_orders')">{{ data.settlement_orders_amount }}</td>
              <td
                class="stats-tooltip"
                @click="showStatsPopup(data.id, 'ended_orders')">{{ data.ended_orders_count }}</td>
              <td
                class="stats-tooltip"
                @click="showStatsPopup(data.id, 'ended_orders')">{{ data.ended_orders_amount }}</td>
            </tr>
            <tr v-if="statistics.data.length === 0">
              <td
                colspan="9"
                class="text-center">
                {{ $t('ui.noInfoToDisplay') }}&hellip;
              </td>
            </tr>

            <tr
              v-for="(type, index) of ['avg', 'min', 'max']"
              :key="'stats-sum-row-' + index"
              class="table-secondary">
              <td class="align-middle"><strong>{{ $t('stats.' + type + 'RowTitle') }}</strong></td>
              <td><strong>{{ Number(statistics.stats[type].offers_count).toFixed(4) }}</strong></td>
              <td><strong>{{ statistics.stats[type].offers_amount }}</strong></td>
              <td><strong>{{ Number(statistics.stats[type].orders_count).toFixed(4) }}</strong></td>
              <td><strong>{{ statistics.stats[type].orders_amount }}</strong></td>
              <td><strong>{{ Number(statistics.stats[type].offers_to_orders_count).toFixed(4) }}</strong></td>
              <td><strong>{{ statistics.stats[type].offers_to_orders_amount }}</strong></td>
              <td><strong>{{ Number(statistics.stats[type].settlement_orders_count).toFixed(4) }}</strong></td>
              <td><strong>{{ statistics.stats[type].settlement_orders_amount }}</strong></td>
              <td><strong>{{ Number(statistics.stats[type].ended_orders_count).toFixed(4) }}</strong></td>
              <td><strong>{{ statistics.stats[type].ended_orders_amount }}</strong></td>
            </tr>
          </template>
        </tbody>
      </table>

      <div
        v-if="!loaded"
        class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <d-modal
      v-if="statsPopupDisplay"
      size="lg"
      @close="closeStatsPopup">
      <d-modal-header>
        <d-modal-title>

        </d-modal-title>
      </d-modal-header>
      <d-modal-body class="p-0">
        <d-alert
          v-if="!statsInPopupLoaded && !statsInPopupError"
          show
          theme="light"
          class="text-center">
          {{ $t('ui.dataLoading') }}
        </d-alert>
        <d-alert
          v-if="statsInPopupError"
          show
          theme="danger"
          class="text-center">
          {{ $t('ui.dataLoadingFailed') }}
        </d-alert>
        <d-alert
          v-if="statsInPopupLoaded && !statsInPopup.data.length"
          show
          theme="warning"
          class="text-center">
          {{ $t('purchase.noOrdersToDisplay') }}
        </d-alert>
        <table
          v-if="statsInPopupLoaded && statsInPopup.data.length"
          class="table">
          <thead>
            <tr>
              <th>{{ $t('ui.orderName') }}</th>
              <th>{{ $t('status.status') }}</th>
              <th>{{ $t('dashboard.customersPopup.customerName') }}</th>
              <th>{{ $t('stats.sumRowTitle') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(order, index) of statsInPopup.data"
              :key="'stats-in-popup-' + index">
              <td>
                <small>{{ order.order_name }}</small><br>
                <template v-if="order.settlements && order.settlements.length">
                  <small v-if="order.settlements.length === 1">
                    {{ $t('statistics.invoice') }}:
                    <span class="text-primary">
                      {{ order.settlements[0].number }}
                    </span>
                  </small>
                  <small v-if="order.settlements.length > 1">
                    {{ $t('statistics.invoices') }}:
                    <span class="text-primary">
                      {{ order.settlements.map(settlement => settlement.number).join(', ') }}
                    </span>
                  </small>
                </template>
                <template v-else>
                  <small class="text-danger">
                    {{ $t('statistics.noInvoices') }}
                  </small>
                </template>
              </td>
              <td><status-badge :status="order.status" /></td>
              <td>
                <small>
                  {{ order.customer_name }}
                </small>

                <template v-if="order.customer_region_name">
                  <br>
                  <small>
                    {{ $t('settings.regions.region') }}:
                    <span class="text-primary">
                      {{ order.customer_region_name }}
                    </span>
                  </small>
                </template>
              </td>
              <td><small>{{ order.stats_price }}</small></td>
            </tr>
          </tbody>
        </table>
      </d-modal-body>
    </d-modal>
  </d-container>
</template>

<script>
import CalendarUtils from '@/utils/CalendarUtils.js';
import FormUtils from '@/utils/FormUtils.js';
import StatusBadge from '@/components/common/StatusBadge.vue';

export default {
  name: 'statistics',
  components: {
    'status-badge': StatusBadge
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedWorkers && this.loadedGroups && this.loadedRegions;
    },
    isCompareMode () {
      if (!this.activeFilters.period_to_compare) {
        return false;
      }
      let periodToCompare = this.activeFilters.period_to_compare;
      return periodToCompare.startDate && periodToCompare.endDate;
    },
    orderWorkerOptions () {
      return this.workers.map(worker => ({
        text: worker.name,
        value: worker.id
      }));
    },
    groupOptions () {
      return this.groups.map(group => ({
        text: group.name,
        value: group.id
      }));
    },
    regionsOptions () {
      return [{ value: null, text: this.$t('settings.regions.allRegions') }].concat(this.regions.map(region => ({
        value: region.id,
        text: region.name
      })));
    },
    filtersConfig () {
      let currentDate = new Date();
      let firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

      return [
        {
          key: 'userOrGroup',
          label: this.$t('users.worker') + ' / ' + this.$t('settings.userGroups.userGroup'),
          type: 'toggle',
          isBasic: true,
          defaultValue: 'user',
          maxWidth: '250px',
          options: [
            { text: this.$t('users.worker'), value: 'user' },
            { text: this.$t('settings.userGroups.userGroup'), value: 'group' }
          ]
        },
        {
          key: 'user_id',
          label: this.$t('users.worker'),
          placeholder: this.$t('order.isWorker'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          multiple: true,
          searchable: false,
          options: this.orderWorkerOptions,
          reduce: option => option,
          visible: (filters) => filters.userOrGroup === 'user'
        },
        {
          key: 'group_id',
          label: this.$t('settings.userGroups.userGroup'),
          placeholder: this.$t('settings.userGroups.userGroup') + '?',
          type: 'select',
          isBasic: true,
          defaultValue: null,
          searchable: false,
          options: this.groupOptions,
          reduce: option => option,
          visible: (filters) => filters.userOrGroup === 'group'
        },
        {
          key: 'period',
          label: this.$t('kpi.period'),
          placeholder: this.$t('kpi.period'),
          type: 'daterange',
          isBasic: true,
          clearable: false,
          defaultValue: { startDate: firstDayOfMonth, endDate: currentDate }
        },
        {
          key: 'period_to_compare',
          label: this.$t('stats.comparePeriods'),
          placeholder: this.$t('stats.comparePeriods'),
          type: 'daterange',
          isBasic: false,
          defaultValue: { startDate: null, endDate: null }
        },
        {
          key: 'region_id',
          label: this.$t('settings.regions.region'),
          placeholder: this.$t('settings.regions.allRegions'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          searchable: false,
          options: this.regionsOptions
        },
        {
          key: 'period_customers',
          label: this.$t('stats.compareCustomers'),
          placeholder: this.$t('stats.compareCustomers'),
          type: 'daterange',
          isBasic: false,
          defaultValue: { startDate: null, endDate: null }
        }
      ];
    }
  },
  data () {
    return {
      statsFields: [
        'offers_count',
        'offers_amount',
        'orders_count',
        'orders_amount',
        'offers_to_orders_count',
        'offers_to_orders_amount',
        'settlement_orders_count',
        'settlement_orders_amount',
        'ended_orders_count',
        'ended_orders_amount'
      ],
      statistics: [],
      loaded: false,
      loadError: false,
      loadedWorkers: false,
      loadedGroups: false,
      loadedRegions: false,
      downloadInProgress: false,
      workers: [],
      groups: [],
      regions: [],
      statsPopupDisplay: false,
      statsInPopup: [],
      statsInPopupLoaded: false,
      statsInPopupError: false,
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  mounted () {
    this.loadAdditionalData();
  },
  methods: {
    handleFiltersInit (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.loadFilteredData();
    },
    loadFilteredData () {
      this.loaded = false;
      let whereData = {};
      let endpointUrl = '/stats/items';
      let paramsData = {};
      let filters = this.activeFilters;

      let hasPeriod = filters.period && filters.period.startDate && filters.period.endDate;
      let hasCompare = filters.period_to_compare && filters.period_to_compare.startDate && filters.period_to_compare.endDate;

      if (hasCompare && hasPeriod) {
        endpointUrl = '/stats/compare';
        whereData.periods = [
          {
            date_from: this.formatDate(filters.period.startDate),
            date_to: this.formatDate(filters.period.endDate)
          },
          {
            date_from: this.formatDate(filters.period_to_compare.startDate),
            date_to: this.formatDate(filters.period_to_compare.endDate)
          }
        ];

        if (filters.user_id !== null && filters.user_id && filters.user_id.length) {
          whereData.periods[0].user_id = [];
          whereData.periods[1].user_id = [];

          for (let i = 0; i < filters.user_id.length; i++) {
            whereData.periods[0].user_id.push(filters.user_id[i].value);
            whereData.periods[1].user_id.push(filters.user_id[i].value);
          }
        }

        paramsData = {
          periods: whereData.periods
        };
      } else {
        if (hasPeriod) {
          whereData.date_from = this.formatDate(filters.period.startDate);
          whereData.date_to = this.formatDate(filters.period.endDate);
        }

        if (filters.userOrGroup === 'user' && filters.user_id !== null && filters.user_id && filters.user_id.length) {
          whereData.user_id = [];
          for (let i = 0; i < filters.user_id.length; i++) {
            whereData.user_id.push(filters.user_id[i].value);
          }
        }

        if (filters.userOrGroup === 'group' && filters.group_id !== null && filters.group_id && filters.group_id.value) {
          whereData.group_id = filters.group_id.value;
        }

        paramsData = {
          where: whereData
        };
      }

      let hasCustomerPeriod = filters.period_customers && (filters.period_customers.startDate || filters.period_customers.endDate);
      if (hasCustomerPeriod) {
        if (!paramsData.where) {
          paramsData.where = whereData;
        }
        paramsData.where.filter_customers = true;

        if (filters.period_customers.startDate) {
          whereData.customer_from = this.formatDate(filters.period_customers.startDate);
        }
        if (filters.period_customers.endDate) {
          whereData.customer_to = this.formatDate(filters.period_customers.endDate);
        }
      }

      if (filters.region_id) {
        whereData.customer_region_id = filters.region_id;
      }

      FormUtils.loadAdditionalData(this, {
        endpoint: endpointUrl,
        outputKey: 'statistics',
        errorKey: 'loadError',
        loadedKey: 'loaded',
        noPagination: true,
        params: paramsData
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/customers/regions/items',
        loadedKey: 'loadedRegions',
        outputKey: 'regions',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/users/items',
        loadedKey: 'loadedWorkers',
        params: {
          return: 'get',
          where: {
            type: ['worker', 'admin']
          }
        },
        outputKey: 'workers',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/users/groups/items',
        loadedKey: 'loadedGroups',
        outputKey: 'groups',
        errorKey: 'loadError',
        params: {
          return: 'get'
        }
      });
    },
    showStatsPopup (id, field) {
      this.statsPopupDisplay = true;
      this.statsInPopupLoaded = false;
      this.statsInPopupError = false;
      this.statsInPopup = [];

      let filters = this.activeFilters;
      let dateFrom = filters.period && filters.period.startDate ? this.formatDate(filters.period.startDate) : null;
      let dateTo = filters.period && filters.period.endDate ? this.formatDate(filters.period.endDate) : null;

      this.loadStatsInPopup(id, field, dateFrom, dateTo, filters.region_id);
    },
    closeStatsPopup () {
      this.statsPopupDisplay = false;
      this.statsInPopupLoaded = false;
      this.statsInPopupError = false;
      this.statsInPopup = [];
    },
    loadStatsInPopup (id, field, dateFrom, dateTo, regionID) {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/stats/orders',
        outputKey: 'statsInPopup',
        errorKey: 'statsInPopupError',
        loadedKey: 'statsInPopupLoaded',
        noPagination: true,
        params: {
          where: {
            user_id: id,
            field,
            date_from: dateFrom,
            date_to: dateTo,
            customer_region_id: regionID
          }
        }
      });
    },
    downloadXlsx () {
      if (this.downloadInProgress) {
        return;
      }

      let filters = this.activeFilters;
      let whereData = {};

      if (filters.period && filters.period.startDate && filters.period.endDate) {
        whereData.date_from = this.formatDate(filters.period.startDate);
        whereData.date_to = this.formatDate(filters.period.endDate);
      }

      if (filters.userOrGroup === 'user' && filters.user_id !== null && filters.user_id && filters.user_id.length) {
        whereData.user_id = [];
        for (let i = 0; i < filters.user_id.length; i++) {
          whereData.user_id.push(filters.user_id[i].value);
        }
      }

      if (filters.userOrGroup === 'group' && filters.group_id !== null && filters.group_id && filters.group_id.value) {
        whereData.group_id = filters.group_id.value;
      }

      if (filters.region_id) {
        whereData.customer_region_id = filters.region_id;
      }

      let paramsData = {
        where: whereData
      };

      this.downloadInProgress = true;

      this.$http.post('/stats/export', paramsData, {
        responseType: 'blob'
      }).then((response) => {
        let link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([response.data]));
        link.setAttribute('download', this.$t('stats.statsFilename') + '.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadInProgress = false;
      }).catch(() => {
        this.downloadInProgress = false;
      });
    },
    formatDate (date) {
      return CalendarUtils.formatDateForPHP(new Date(date));
    }
  }
};
</script>

<style lang="scss">
.statistics .fixed-thead,
.statistics .fixed-row {
  box-shadow: 0 5px 5px rgba(0, 0, 0, .1);
  position: sticky;
  top: 0;
}

.statistics .fixed-row {
  top: 83px;
}

.statistics .stats-tooltip {
  &:hover {
    background: #f0f0f0;
    cursor: pointer;
  }
}

.text-success {
  .material-icons.is-dec,
  .material-icons.is-eq {
    display: none;
  }
}

.text-danger {
  .material-icons.is-inc,
  .material-icons.is-eq {
    display: none;
  }
}

.text-gray {
  .material-icons.is-inc,
  .material-icons.is-dec {
    display: none;
  }
}
</style>
